import { useState, useRef, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiRequest } from "@/lib/queryClient";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Bell, BellOff, CalendarDays, Link2, Repeat, User, X } from "lucide-react";
import { taskLinkLabel, type TaskLink } from "@shared/taskLinks";
import { RecordLinkPicker } from "./RecordLinkPicker";

/**
 * Capture, not a form.
 *
 * This replaces both the "New Task" dialog and desktop ReminderPicker. Those
 * were two dialogs that could not see each other — one could set a priority but
 * no alarm, the other an alarm but no priority — which is what made tasks and
 * reminders feel like competing tools.
 *
 * The design rule: typing a line and pressing Enter must stay a two-second
 * action. Everything else appears on focus and is optional, so the full form is
 * one click away and never in the way. Priority and description are deliberately
 * NOT here; they are refinements and live in the row's detail panel.
 */

type Preset = "none" | "today" | "tomorrow" | "monday" | "custom";

const PRESET_LABEL: Record<Preset, string> = {
  none: "No date",
  today: "Today 5pm",
  tomorrow: "Tomorrow 9am",
  monday: "Next Monday 9am",
  custom: "Pick a date…",
};

/** Local wall-clock for a preset, as a value the <input type=datetime-local> understands. */
function presetToLocal(preset: Preset): string {
  const d = new Date();
  if (preset === "today") {
    d.setHours(17, 0, 0, 0);
  } else if (preset === "tomorrow") {
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
  } else if (preset === "monday") {
    // 0 = Sunday. Always lands on the NEXT Monday, never today.
    const daysUntilMonday = ((8 - d.getDay()) % 7) || 7;
    d.setDate(d.getDate() + daysUntilMonday);
    d.setHours(9, 0, 0, 0);
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function QuickAddBar() {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [preset, setPreset] = useState<Preset>("none");
  const [when, setWhen] = useState("");
  const [remind, setRemind] = useState(false);
  const [assignedToId, setAssignedToId] = useState<string>("me");
  const [links, setLinks] = useState<TaskLink[]>([]);
  const [repeat, setRepeat] = useState<string>("none");

  const { data: users } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["/api/users"],
    queryFn: async () => (await apiRequest("GET", "/api/users")).json(),
    enabled: expanded,
  });

  const reset = () => {
    setTitle("");
    setPreset("none");
    setWhen("");
    setRemind(false);
    setAssignedToId("me");
    setLinks([]);
    setRepeat("none");
    setExpanded(false);
  };

  const create = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { title: title.trim() };
      if (when) {
        // datetime-local has no zone; the browser reads it as local, which is
        // what the user meant.
        body.dueAt = new Date(when).toISOString();
        body.remind = remind;
      }
      if (assignedToId !== "me") body.assignedToId = assignedToId;
      if (links.length) body.links = links.map(({ kind, id }) => ({ kind, id }));
      if (repeat !== "none") body.recurrenceRule = repeat;
      return (await apiRequest("POST", "/api/tasks", body)).json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/tasks"] });
      qc.invalidateQueries({ queryKey: ["/api/reminders"] });
      reset();
      // Straight back to capturing — a to-do list is used in bursts.
      inputRef.current?.focus();
    },
    onError: (e: any) =>
      toast.error(String(e.message ?? "").replace(/^\d+:\s*/, "") || "Could not add that."),
  });

  const submit = () => {
    if (!title.trim() || create.isPending) return;
    create.mutate();
  };

  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (p === "none") {
      setWhen("");
      setRemind(false);
    } else {
      setWhen(presetToLocal(p === "custom" ? "tomorrow" : p));
      // A date the user chose deliberately usually wants the alarm too. They
      // can turn it off; the point is not to make them turn it on every time.
      if (p !== "custom") setRemind(true);
    }
  };

  // Escape collapses without losing what was typed.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded]);

  const whenLabel = when
    ? new Date(when).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : PRESET_LABEL[preset];

  return (
    <div
      className={cn(
        "rounded-lg border bg-card transition-colors",
        expanded ? "border-primary/40 shadow-sm" : "border-border",
      )}
    >
      <div className="flex items-center gap-2 px-3 py-2.5">
        <span className="text-lg leading-none text-muted-foreground">+</span>
        <Input
          ref={inputRef}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onFocus={() => setExpanded(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Add a to-do…"
          className="h-8 border-0 bg-transparent px-0 text-sm shadow-none focus-visible:ring-0"
          data-testid="quick-add-input"
        />
        {expanded && (
          <Button size="sm" className="h-8" onClick={submit} disabled={!title.trim() || create.isPending}>
            {create.isPending ? "Adding…" : "Add"}
          </Button>
        )}
      </div>

      {expanded && (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-border/60 px-3 py-2">
          {/* When */}
          <Select value={preset} onValueChange={(v) => applyPreset(v as Preset)}>
            <SelectTrigger className="h-7 w-auto gap-1.5 border-border/70 px-2 text-xs">
              <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
              <SelectValue>{whenLabel}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {(["none", "today", "tomorrow", "monday", "custom"] as Preset[]).map((p) => (
                <SelectItem key={p} value={p}>
                  {PRESET_LABEL[p]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {preset === "custom" && (
            <Input
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
              className="h-7 w-[190px] text-xs"
            />
          )}

          {/* The bell. Meaningless without a date, so it is disabled until there is one. */}
          <button
            type="button"
            onClick={() => setRemind((r) => !r)}
            disabled={!when}
            title={when ? (remind ? "Will remind you" : "No alarm") : "Pick a date first"}
            className={cn(
              "inline-flex h-7 items-center gap-1.5 rounded-md border px-2 text-xs transition-colors",
              !when && "cursor-not-allowed opacity-40",
              remind
                ? "border-primary/40 bg-primary/12 text-primary-text"
                : "border-border/70 text-muted-foreground hover:bg-muted",
            )}
          >
            {remind ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
            {remind ? "Remind me" : "No alarm"}
          </button>

          {/* Assign */}
          <Select value={assignedToId} onValueChange={setAssignedToId}>
            <SelectTrigger className="h-7 w-auto gap-1.5 border-border/70 px-2 text-xs">
              <User className="h-3.5 w-3.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="me">Me</SelectItem>
              {(users ?? []).map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Link to CRM records: as many as the task is about. */}
          <RecordLinkPicker value={links} onChange={setLinks}>
            <button
              type="button"
              title={links.map(taskLinkLabel).join("\n") || undefined}
              className={cn(
                "inline-flex h-7 max-w-[220px] items-center gap-1.5 rounded-md border px-2 text-xs transition-colors",
                links.length
                  ? "border-primary/40 bg-primary/12 text-primary-text"
                  : "border-border/70 text-muted-foreground hover:bg-muted",
              )}
            >
              <Link2 className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{links.length ? taskLinkLabel(links[0]) : "Link…"}</span>
              {links.length > 1 && <span className="shrink-0 tabular-nums">+{links.length - 1}</span>}
              {links.length > 0 && (
                <X
                  className="h-3 w-3 shrink-0"
                  aria-label="Clear links"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLinks([]);
                  }}
                />
              )}
            </button>
          </RecordLinkPicker>

          {/* Repeat */}
          <Select value={repeat} onValueChange={setRepeat}>
            <SelectTrigger className="h-7 w-auto gap-1.5 border-border/70 px-2 text-xs">
              <Repeat className="h-3.5 w-3.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No repeat</SelectItem>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekdays">Weekdays</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="biweekly">Every 2 weeks</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>

          <button
            type="button"
            onClick={reset}
            className="ml-auto px-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
